import { useState } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import {
  Package, Users, Truck, Home, Stethoscope, HelpCircle,
  MapPin, Clock, Heart, Crown, Share2,
} from "lucide-react";
import { triggerRoyalCelebration, triggerVolunteerCelebration } from "./RoyalCelebration";
import { SpeakButton } from "./SpeakButton";
import { ShareButton } from "./ShareButtons";

const C = {
  navy: "#2D3561", white: "#FAFAF8", orange: "#F5A623",
  blue: "#4A90D9", red: "#E74C3C", pink: "#F78DA7",
  lilac: "#B39DDB", gray: "#6B7280", lightgray: "#9CA3AF",
};
const DISPLAY = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const MONO = "'IBM Plex Mono', 'Courier New', monospace";

const ADMIN_EMAILS = ["dra.querol@nuriaquerol.com"];

const TYPE_CONFIG = {
  material: { label: "Material", icon: Package, color: C.orange },
  voluntarios: { label: "Voluntarios", icon: Users, color: C.blue },
  transporte: { label: "Transporte", icon: Truck, color: C.lilac },
  acogida: { label: "Acogida", icon: Home, color: "#81801a" },
  veterinario: { label: "Veterinario", icon: Stethoscope, color: C.pink },
  otro: { label: "Otro", icon: HelpCircle, color: C.gray },
};

const URGENCY_CONFIG = {
  urgente: { label: "Urgente", color: C.red, bg: C.red },
  prioridad: { label: "Prioridad", color: C.orange, bg: C.orange },
  activo: { label: "Activo", color: "#81801a", bg: "#81801a" },
  info: { label: "Info", color: C.blue, bg: C.blue },
};

export default function NeedCard({ need, onOfferSent }) {
  const { user } = useAuth();
  const [showOffer, setShowOffer] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const typeInfo = TYPE_CONFIG[need.type] || TYPE_CONFIG.otro;
  const urgencyInfo = URGENCY_CONFIG[need.urgency] || URGENCY_CONFIG.activo;
  const TypeIcon = typeInfo.icon;

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}min`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  const handleOffer = async () => {
    if (!user) { toast.error("Inicia sesión para ofrecer ayuda"); return; }
    setSending(true);
    try {
      const token = localStorage.getItem("ea_token");
      const statsRes = await axios.get(`${API}/offers/my-stats`, { headers: { Authorization: `Bearer ${token}` } });
      const isFirstOffer = statsRes.data.total_offers === 0;
      await axios.post(`${API}/offers`, { need_id: need.need_id, message }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });

      if (isFirstOffer) {
        const isRoyalty = ADMIN_EMAILS.includes(user.email);
        if (isRoyalty) {
          triggerRoyalCelebration();
          toast.custom(() => (
            <div className="p-4 flex items-center gap-3" style={{ background: C.lilac, borderRadius: 4 }}>
              <Crown className="w-7 h-7 animate-bounce" style={{ color: C.orange }} />
              <div>
                <p className="font-bold text-base text-white" style={{ fontFamily: DISPLAY }}>Su Majestad se ha dignado a ayudar</p>
                <p className="text-[10px] text-white/70" style={{ fontFamily: MONO }}>Que suene la marcha Radetzky</p>
              </div>
            </div>
          ), { duration: 5000 });
        } else {
          triggerVolunteerCelebration();
          toast.custom(() => (
            <div className="p-4 flex items-center gap-3" style={{ background: C.pink, borderRadius: 4 }}>
              <Heart className="w-7 h-7 text-white animate-pulse" fill="white" />
              <div>
                <p className="font-bold text-base text-white" style={{ fontFamily: DISPLAY }}>Tu primera ayuda</p>
                <p className="text-[10px] text-white/70" style={{ fontFamily: MONO }}>Gracias por hacer del mundo un lugar mejor</p>
              </div>
            </div>
          ), { duration: 4000 });
        }
      } else { toast.success("Oferta de ayuda enviada"); }

      setShowOffer(false);
      setMessage("");
      onOfferSent?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al enviar oferta"); }
    finally { setSending(false); }
  };

  return (
    <>
      <div
        data-testid={`need-card-${need.need_id}`}
        className="p-5 transition-all duration-200 hover:-translate-y-0.5"
        style={{ background: C.white, border: `1px solid ${C.navy}08`, borderRadius: 4 }}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="w-9 h-9 flex items-center justify-center" style={{ background: `${typeInfo.color}12`, borderRadius: 4 }}>
            <TypeIcon className="w-4.5 h-4.5" style={{ color: typeInfo.color }} />
          </div>
          <span className="text-[9px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 text-white"
            style={{ fontFamily: MONO, background: urgencyInfo.bg, borderRadius: 3 }}>
            {urgencyInfo.label}
          </span>
        </div>

        <h3 className="text-base font-bold mb-1 line-clamp-2" style={{ fontFamily: DISPLAY, color: C.navy }}>{need.title}</h3>
        <p className="text-sm mb-3 line-clamp-2 leading-relaxed" style={{ color: C.gray }}>{need.description}</p>

        {need.quantity && (
          <p className="text-[10px] mb-3 tracking-wider" style={{ fontFamily: MONO, color: C.blue }}>Cantidad: {need.quantity}</p>
        )}

        <div className="flex items-center gap-3 text-[10px] mb-4" style={{ fontFamily: MONO, color: C.lightgray }}>
          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{need.location_name || "Sin ubicación"}</span>
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{timeAgo(need.created_at)}</span>
          <div className="flex items-center gap-1 ml-auto">
            <ShareButton
              title={need.title}
              text={`${need.urgency === "urgente" ? "URGENTE" : need.urgency === "prioridad" ? "PRIORIDAD" : ""} SE NECESITA: ${typeInfo.label}\n${need.location_name || 'España'}\n\n${need.title}\n${need.description || ''}\n\nTu ayuda puede marcar la diferencia\n#EmergenciaAnimal #Animales`}
              url={`${window.location.origin}/needs?need=${need.need_id}`}
              variant="ghost" size="sm" showLabel={false} className="h-7 w-7 p-0"
            />
            <SpeakButton
              text={`Necesidad ${urgencyInfo.label}. ${typeInfo.label}. ${need.title}. ${need.description}. ${need.location_name ? `En ${need.location_name}.` : ''} De ${need.shelter_name}.`}
            />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-[10px]" style={{ fontFamily: MONO, color: C.lightgray }}>
            {need.shelter_name} / {need.offers_count || 0} ofertas
          </span>
          {user && need.status === "abierta" && need.shelter_id !== user.user_id && (
            <button
              onClick={() => setShowOffer(true)}
              className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white transition-all duration-200 hover:-translate-y-0.5 flex items-center gap-1.5"
              style={{ fontFamily: MONO, background: C.navy, borderRadius: 4 }}
              data-testid={`offer-help-btn-${need.need_id}`}
            >
              <Heart className="w-3 h-3" /> Ayudar
            </button>
          )}
        </div>
      </div>

      {/* Offer Dialog */}
      {showOffer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowOffer(false)}>
          <div className="p-6 w-full max-w-md mx-4" style={{ background: C.white, borderRadius: 6, border: `1px solid ${C.navy}12` }} onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-1" style={{ fontFamily: DISPLAY, color: C.navy }}>Ofrecer ayuda</h3>
            <p className="text-[10px] mb-4" style={{ fontFamily: MONO, color: C.lightgray }}>
              {need.shelter_name}: {need.title}
            </p>
            <Textarea
              value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Mensaje opcional..."
              className="mb-4" style={{ border: `1px solid ${C.navy}12`, borderRadius: 4 }}
              data-testid="offer-message-input"
            />
            <div className="flex gap-3">
              <button onClick={() => setShowOffer(false)}
                className="flex-1 py-2.5 text-[10px] font-bold uppercase tracking-wider transition-all"
                style={{ fontFamily: MONO, background: `${C.navy}06`, color: C.navy, borderRadius: 4 }}>
                Cancelar
              </button>
              <button onClick={handleOffer} disabled={sending}
                className="flex-1 py-2.5 text-[10px] font-bold uppercase tracking-wider text-white transition-all duration-200 hover:-translate-y-0.5"
                style={{ fontFamily: MONO, background: C.navy, borderRadius: 4 }}
                data-testid="confirm-offer-btn">
                {sending ? "Enviando..." : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
